import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sprout } from "lucide-react";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import TopBar from "@/components/TopBar";
import { T } from "@/context/TextEditContext";

const LOGO_IMG =
  "https://customer-assets.emergentagent.com/job_mobile-food-app-4/artifacts/isog56bh_Blossom..png";

/**
 * AboutPage — customer-facing "Our Story & Mission" page.
 * Copy is fully admin-editable via Site Content -> About Us so the owner
 * can tune it any time without a redeploy.
 */
export default function AboutPage() {
  const navigate = useNavigate();
  const { settings } = useSiteSettings();

  const headline =
    settings.about_headline || "Made with love, seasoned with heart.";
  const story = settings.about_story || "";
  const foundedYear = settings.about_founded_year || "";

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="about-page">
      <TopBar onOpenCart={() => navigate("/checkout")} />

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-8">
        <button
          onClick={() => navigate(-1)}
          data-testid="about-back-btn"
          className="text-brand-red text-xs uppercase tracking-widest font-bold flex items-center gap-1 hover:underline"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <T k="about.back" d="Back" />
        </button>

        {/* Hero */}
        <section className="chalkboard-band rounded-2xl overflow-hidden border-4 border-brand-red">
          <div className="px-6 py-8 text-center space-y-4">
            <div
              className="w-24 h-24 mx-auto rounded-full bg-cover bg-center border-4 border-brand-yellow shadow-lg"
              style={{ backgroundImage: `url(${LOGO_IMG})` }}
              aria-label="Blossom's Kitchen logo"
            />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-black">
                <T k="about.badge" d="Our Story" />
              </div>
              <h1
                className="font-hand text-brand-yellow text-4xl sm:text-5xl leading-tight mt-1"
                data-testid="about-headline"
              >
                <T field="about_headline" d="Made with love, seasoned with heart." />
              </h1>
              {foundedYear && (
                <div className="text-[11px] uppercase tracking-widest text-white/80 font-bold mt-2">
                  <T k="about.since_prefix" d="Serving hearts since" /> {foundedYear}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Story */}
        {story && (
          <section
            data-testid="about-story-section"
            className="bg-white rounded-2xl border-2 border-stone-200 p-6 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center">
                <Sprout className="w-5 h-5" />
              </div>
              <h2 className="font-display text-brand-red text-2xl leading-none">
                <T k="about.story_title" d="Our Story" />
              </h2>
            </div>
            <p className="text-stone-700 leading-relaxed whitespace-pre-line">
              <T field="about_story" />
            </p>
          </section>
        )}

        {/* Mission section removed per owner request. `about_mission`
            still exists in site_settings but isn't rendered anywhere on
            the customer-facing About page. */}

        {/* CTA back to menu */}
        <div className="text-center py-4">
          <button
            onClick={() => navigate("/")}
            data-testid="about-cta-menu"
            className="bg-brand-red text-white px-6 py-3 rounded-full text-sm uppercase tracking-widest font-black shadow-lg hover:bg-red-800 active:scale-95 transition-all"
          >
            <T k="about.cta" d="See today's menu →" />
          </button>
        </div>
      </div>
    </div>
  );
}
